import React, {
  useState, useMemo, useEffect, useRef,
} from 'react';

import {
  Text, Stack, Flex, HStack,
} from '@chakra-ui/react';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';

import { getUser } from 'api/users';
import { Button, Icon } from 'components';
import { breadcrumbs } from 'constants/routes';
import BreadcrumbMenu from 'containers/Breadcrumb';
import Page from 'containers/Page/index';
import { DocumentCard, UserInfo } from 'containers/User';
import Password from 'containers/User/Password';
import { ContentRoutes } from 'enums/routes';
import { AccessTypes, MethodTypes, UserTypes } from 'enums/types';
import { useFormModal } from 'hooks/useFormModal';
import { policy } from 'polices';
import useUser from 'stores/useUser';

export default function User() {
  const { id } = useParams<GeneralRoute>();
  const { user: loggedUser, setUser } = useUser();
  const [user, setUserState] = useState<User>();
  const type = user?.type === UserTypes.Driver ? ContentRoutes.Drivers : ContentRoutes.Users;
  const { onOpen, setUpdateId, Form } = useFormModal(type);
  const userRef = useRef(useUser.getState().user);
  const policies = policy(type);
  useEffect(() => {
    mutate();

    useUser.subscribe((state) => {
      userRef.current = state.user;

      return null;
    });
  }, []);
  const { isLoading, mutate } = useMutation(() => getUser({ id: id || loggedUser?.id }), {
    onSuccess: ({ data }) => {
      if (data.id === loggedUser?.id) {
        setUser(data);
      }
      setUserState(data);
    },
  });

  const isProfile = useMemo(() => window.location.pathname.includes('profile'), []);

  return (
    <Page isLoading={isLoading}>
      {user && (
        <Stack
          w="1024px"
          alignSelf="center">
          <Form onSuccess={mutate} />
          <BreadcrumbMenu paths={[
            ...breadcrumbs[type],
            {
              label: user.name,
              path: window.location.pathname,
            },
          ]}
          />
          <Flex
            width="100%"
            justifyContent="space-between">
            <Text variant="3xLargeBold">{isProfile ? 'Meu Perfil' : user.name}</Text>
            {(policies(MethodTypes.Update) !== AccessTypes.Disallow || isProfile) && (
            <Button
              shadow={1}
              onClick={() => {
                setUpdateId(user?.id);
                onOpen();
              }}
              bg="white.default"
              _hover={{
                bg: 'primary.700',
                color: 'white.default',
              }}
              borderRadius={50}
              h={8}
              _active={{
                color: 'white.default',
              }}
              color="gray.500">
              <Text variant="xSmallSemiBold">Editar</Text>
              <Icon
                ml={2}
                name="edit"
              />
            </Button>
            )}
          </Flex>
          <Stack spacing={5}>
            <UserInfo
              user={user}
            />
            {isProfile && <Password />}
          </Stack>
          {user.type === UserTypes.Driver && (
          <HStack>
            <DocumentCard
              mutate={mutate}
              documentName="documentNumber"
              ImageUrlName="documentImageUrl"
              dueDateName="documentNumberDueDate"
              user={user}
              label="CNH"
              pt={7}
            />
            <DocumentCard
              mutate={mutate}
              documentName="ear"
              ImageUrlName="earImageUrl"
              dueDateName="earDueDate"
              user={user}
              label="EAR"
              pt={7}
            />
            <DocumentCard
              mutate={mutate}
              documentName="mopp"
              ImageUrlName="moppImageUrl"
              dueDateName="moppDueDate"
              user={user}
              label="MOPP"
              pt={7}
            />
          </HStack>
          )}
        </Stack>
      )}
    </Page>
  );
}
